import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import storage from './lib/storage';

// Make Pusher globally available (required by Echo)
declare global {
    interface Window { 
        Pusher: typeof Pusher; 
        Echo: any; 
    }
}
window.Pusher = Pusher;

// Enable Pusher logging for debugging
Pusher.logToConsole = true;

// Get configuration from environment variables
const REVERB_APP_KEY = import.meta.env.VITE_REVERB_APP_KEY;
const REVERB_HOST = import.meta.env.VITE_REVERB_HOST;
const REVERB_PORT = Number(import.meta.env.VITE_REVERB_PORT);

if (!REVERB_APP_KEY) {
    console.error('Reverb configuration missing in .env file (VITE_REVERB_APP_KEY) or could not be read.');
}

// Declare echo variable but don't initialize yet
let echo: any;
let isInitializing = false;
let hasInitialized = false;

// Initialize Echo with proper configuration
const initializeEcho = async (): Promise<any> => {
    // Prevent duplicate initialization
    if (isInitializing) {
        console.log('Echo is already initializing, waiting...');
        // Wait for current initialization to complete
        while (isInitializing) {
            await new Promise(resolve => setTimeout(resolve, 100));
        }
        return echo;
    }

    if (hasInitialized && echo) {
        console.log('Echo already initialized, returning existing instance');
        return echo;
    }

    isInitializing = true;
    
    try {
        console.log('🔄 Initializing Echo...');
        
        // Get token and API URL from storage
        const [token, apiUrl, tenantUuid] = await Promise.all([
            storage.getItem('onespace_token'),
            storage.getItem('onespace_external_api'),
            storage.getItem('onespace_tenant_uuid')
        ]);

        if (!token || !apiUrl) {
            console.error('Missing required Echo configuration');
            return null;
        }

        // Construct the correct auth endpoint URL
        const authEndpoint = `${apiUrl}/broadcasting/auth`;
        console.log('Using auth endpoint:', authEndpoint);

        // Create the Echo instance with proper authentication
        const echoInstance = new Echo({
            broadcaster: 'reverb',
            key: REVERB_APP_KEY,
            authEndpoint: authEndpoint,
            wsHost: REVERB_HOST,
            wsPort: REVERB_PORT,
            wssPort: REVERB_PORT,
            forceTLS: true,
            enabledTransports: ['ws', 'wss'],
            enableStats: false,
            auth: {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'X-Tenant-UUID': tenantUuid || ''
                }
            }
        });

        // Debug connection events
        try {
            if (echoInstance.connector && 'pusher' in echoInstance.connector) {
                echoInstance.connector.pusher.connection.bind('connected', () => {
                    console.log('✅ Successfully connected to Echo server!');
                });

                echoInstance.connector.pusher.connection.bind('error', (error: any) => {
                    console.error('❌ Echo connection error:', error);
                });
            }
        } catch (error) {
            console.error('Error setting up connection listeners:', error);
        }

        echo = echoInstance;
        hasInitialized = true;
        console.log('✅ Echo initialized successfully');
        
        return echoInstance;
    } catch (error) {
        console.error('❌ Error initializing Echo:', error);
        return null;
    } finally {
        isInitializing = false;
    }
};

// Export the initialization function
export const getEchoInstance = async (): Promise<any> => {
    if (!echo || !hasInitialized) {
        return await initializeEcho();
    }
    return echo;
};

// Export a function to get the current echo instance (sync)
export const getEcho = (): any => {
    return echo;
};

// Export a method to manually reinitialize Echo if needed
export const reinitializeEcho = async (): Promise<any> => {
    hasInitialized = false;
    echo = null;
    return await initializeEcho();
};

// Default export for backwards compatibility
export default echo;